/**
 * Keycloak puts realm roles under `realm_access.roles` (access token and often ID token).
 * This is a client-side UX gate only; the usage API still enforces staff/tenant via its authorizer.
 */

/** Decode JWT payload without verification (read claims only). */
export function decodeJwtPayload(
  accessToken: string,
): Record<string, unknown> | null {
  try {
    const parts = accessToken.split(".");
    if (parts.length < 2) return null;
    const payloadPart = parts[1];
    if (typeof payloadPart !== "string") return null;
    let b64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (b64.length % 4)) % 4;
    b64 += "=".repeat(pad);
    const json = globalThis.atob(b64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function realmRolesFromClaims(
  claims: Record<string, unknown> | undefined,
): string[] {
  if (!claims) return [];
  const ra = claims.realm_access;
  if (ra == null || typeof ra !== "object" || Array.isArray(ra)) return [];
  const roles = (ra as { roles?: unknown }).roles;
  if (!Array.isArray(roles)) return [];
  return roles.map((r) => String(r));
}

/**
 * True if `roleName` appears in `realm_access.roles` on the ID token profile and/or access token payload.
 */
export function hasPortalRealmRole(
  profile: Record<string, unknown> | undefined,
  accessToken: string | undefined,
  roleName: string,
): boolean {
  if (!roleName) return false;
  if (realmRolesFromClaims(profile).includes(roleName)) return true;
  if (accessToken) {
    const payload = decodeJwtPayload(accessToken);
    if (payload && realmRolesFromClaims(payload).includes(roleName)) return true;
  }
  return false;
}
